/**
 * ElevenLabsApiKeyModal - Modal for entering ElevenLabs API key
 * 
 * Displays when user tries to use voice features without an API key set.
 * Stores the key in the user's Firestore settings.
 */

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel } from './GlassPanel'
import { Button } from './Button'
import { useUserSettings } from '../../hooks/useUserSettings'
import { Icon } from './Icon'
import './ElevenLabsApiKeyModal.css'

/**
 * ElevenLabsApiKeyModal Component
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal closes
 * @param {function} onSuccess - Callback when API key is successfully saved
 */
export function ElevenLabsApiKeyModal({ isOpen, onClose, onSuccess }) {
  const { setElevenLabsApiKey, getElevenLabsApiKey, saving } = useUserSettings()
  const [apiKey, setApiKey] = useState('')
  const [error, setError] = useState(null)
  const [showKey, setShowKey] = useState(false)
  const inputRef = useRef(null)

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Pre-fill with existing key if available
      const existingKey = getElevenLabsApiKey()
      setApiKey(existingKey || '')
      setError(null)
      setShowKey(false)
    }
  }, [isOpen, getElevenLabsApiKey])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const trimmedKey = apiKey.trim()
    
    if (!trimmedKey) {
      setError('Please enter your API key')
      return
    }

    // Basic validation - ElevenLabs keys are typically 32 characters
    if (trimmedKey.length < 20) {
      setError('API key seems too short')
      return
    }

    setError(null)

    try {
      await setElevenLabsApiKey(trimmedKey)
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to save API key:', err)
      setError(err.message || 'Failed to save API key')
    }
  }

  const handleClear = async () => {
    if (window.confirm('Remove your ElevenLabs API key? Voice features will stop working.')) {
      try {
        await setElevenLabsApiKey(null)
        setApiKey('')
        onClose()
      } catch (err) {
        console.error('Failed to clear API key:', err)
        setError(err.message || 'Failed to clear API key')
      }
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="elevenlabs-api-key-modal__overlay" onClick={onClose}>
          <motion.div
            className="elevenlabs-api-key-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <GlassPanel glow="purple" padding="xl">
              <div className="elevenlabs-api-key-modal__header">
                <span className="elevenlabs-api-key-modal__icon"><Icon name="volume" size={20} /></span>
                <h2 className="elevenlabs-api-key-modal__title">ElevenLabs API Key</h2>
              </div>
              
              <p className="elevenlabs-api-key-modal__description">
                Enter your ElevenLabs API key to enable voice suggestion features for characters.
                Your key is stored securely and never shared.
              </p>

              <form onSubmit={handleSubmit} className="elevenlabs-api-key-modal__form">
                <div className="elevenlabs-api-key-modal__field">
                  <label htmlFor="elevenlabs-api-key" className="elevenlabs-api-key-modal__label">
                    API Key
                  </label>
                  <div className="elevenlabs-api-key-modal__input-wrapper">
                    <input
                      ref={inputRef}
                      id="elevenlabs-api-key"
                      type={showKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Enter your ElevenLabs API key..."
                      className="elevenlabs-api-key-modal__input"
                      disabled={saving}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="elevenlabs-api-key-modal__toggle-visibility"
                      onClick={() => setShowKey(!showKey)}
                      title={showKey ? 'Hide key' : 'Show key'}
                    >
                      <Icon name={showKey ? 'eye' : 'eye-off'} size={18} />
                    </button>
                  </div>
                  <p className="elevenlabs-api-key-modal__hint">
                    Get your API key from{' '}
                    <a
                      href="https://elevenlabs.io/app/settings/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="elevenlabs-api-key-modal__link"
                    >
                      elevenlabs.io
                    </a>
                  </p>
                </div>

                {error && (
                  <p className="elevenlabs-api-key-modal__error">{error}</p>
                )}

                <div className="elevenlabs-api-key-modal__actions">
                  {getElevenLabsApiKey() && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleClear}
                      disabled={saving}
                    >
                      Remove Key
                    </Button>
                  )}
                  <div className="elevenlabs-api-key-modal__actions-right">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onClose}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={saving}
                    >
                      Save Key
                    </Button>
                  </div>
                </div>
              </form>
            </GlassPanel>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default ElevenLabsApiKeyModal
